import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Customer } from '../../identity/api';
import { Button, Dialog, TextField } from '@nyce/admin-kit/ui';
import { client, useLogin } from '../../identity/session';
import { sortedByName } from '../../identity/format';
import {
  Code,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  Pager,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { AliasChips, AliasesEditor } from './Aliases';

const PAGE_SIZE = 25;

function CustomerForm({
  initial,
  isNew,
  onSave,
  onClose,
}: {
  initial: Customer;
  isNew: boolean;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(name.trim());
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'New customer' : `Customer ${initial.name}`}
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!name.trim()}>
            Save
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          required
          autoFocus
          hint="Surfaces in every JWT of the customer's tenants as the orgid claim."
          fieldClassName="nyk-span2"
        />
        {!isNew && (
          <div className="nyk-ui-field nyk-span2">
            <span>Id</span>
            <Code>{initial.id}</Code>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** A customer's aliases (`?aliases=<id>`, so the dialog is shareable); read-only without write access. */
function CustomerAliasesDialog({
  id,
  canWrite,
  onChanged,
  onClose,
  notify,
}: {
  id: string;
  canWrite: boolean;
  onChanged: () => Promise<void>;
  onClose: () => void;
  notify: { success: (t: string) => void; error: (t: string) => void };
}) {
  const customer = useAsync(() => client.customers.get(id), [id]);
  const c = customer.data;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Aliases of ${c?.name ?? '…'}`}
      maxWidth={640}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <ErrorBox error={customer.error} />
      <Loading loading={customer.loading && !c} />
      {c && (
        <>
          <p className="nyk-card-intro">
            Ids other systems use for this organization, so their data connects to the customer in AUTH.
          </p>
          <AliasesEditor
            level="customer"
            aliases={c.aliases ?? []}
            canWrite={canWrite}
            replace={async (next) => {
              await client.customers.aliases.replace(c.id, next);
              await Promise.all([customer.reload(), onChanged()]);
            }}
            notify={notify}
          />
        </>
      )}
    </Dialog>
  );
}

/** Customers = the organization a tenant belongs to. No delete: the tenants FK is ON DELETE RESTRICT. */
export function CustomersPage() {
  const login = useLogin();
  const canWrite = !!login?.access.canWrite;
  const [page, setPage] = useState(1);
  const customers = useAsync(() => client.customers.list({ page, pageSize: PAGE_SIZE }), [page]);
  const [editing, setEditing] = useState<{ value: Customer; isNew: boolean } | null>(null);
  const toast = useNotice();
  const [params, setParams] = useSearchParams();
  const aliasesOf = params.get('aliases');
  const showAliases = (id: string | null) =>
    setParams(
      (p) => {
        if (id) p.set('aliases', id);
        else p.delete('aliases');
        return p;
      },
      { replace: true }
    );
  const save = async (name: string) => {
    if (editing?.isNew) {
      const c = await client.customers.create({ name });
      toast.success(`Created ${c.name}`);
    } else if (editing) {
      await client.customers.update(editing.value.id, { name });
      toast.success(`Saved ${name}`);
    }
    await customers.reload();
  };
  return (
    <Page
      title="Customers"
      description="The organizations tenants belong to. A customer's name is the orgid claim of every token its tenants issue."
      actions={
        canWrite && (
          <Button onClick={() => setEditing({ value: { id: '', name: '' }, isNew: true })}>
            New customer
          </Button>
        )
      }
    >
      <ErrorBox error={customers.error} />
      {toast.element}
      <Loading loading={customers.loading} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="customers-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Aliases</th>
                <th>Id</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedByName(customers.data?.items ?? [], (c) => c.name).map((c) => (
                <tr key={c.id} data-customer={c.id}>
                  <td className="nyk-card-title nyk-wrap">
                    <b>{c.name}</b>
                  </td>
                  <td data-label="Aliases">
                    <AliasChips aliases={c.aliases} />
                  </td>
                  <td data-label="Id">
                    <Code>{c.id}</Code>
                  </td>
                  <td className="nyk-row-actions">
                    <Button size="sm" variant="ghost" onClick={() => showAliases(c.id)}>
                      Aliases
                    </Button>
                    {canWrite && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ value: c, isNew: false })}
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!customers.loading && !customers.data?.items.length && (
          <EmptyState
            title="No customers yet."
            hint="Create the organization first; every tenant references one when it is created."
          />
        )}
        {customers.data && (
          <Pager
            page={customers.data.page}
            pageSize={customers.data.pageSize}
            total={customers.data.total}
            onPage={setPage}
          />
        )}
      </div>
      {aliasesOf && (
        <CustomerAliasesDialog
          id={aliasesOf}
          canWrite={canWrite}
          onChanged={customers.reload}
          onClose={() => showAliases(null)}
          notify={toast}
        />
      )}
      {editing && (
        <CustomerForm
          initial={editing.value}
          isNew={editing.isNew}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}
